// src/components/listing-selection-modal/BatchListingSelectionModal.js
import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setSystemNotification } from '../../store/system/system.action';
import { useGetSalesQuery, useAddCardToSaleMutation, useCreateSaleMutation } from '../../entities/sales/sales.api';
import { selectUser } from '../../store/user/user.selector';
import { SALE_STATUSES, isSold } from '../../utils/sales/sales.constants';
import CardAttribute from '../card-attribute/CardAttribute';
import ListingCard from '../../pages/sales-page/components/sales-tracker/components/listing-card/ListingCard';
import addIcon from '../../assets/system/icons/add-icon.png';
import minusIcon from '../../assets/system/icons/minus-icon.png';
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  SetsList,
  ModalFooter,
  ButtonGroup,
  AddButton,
  CancelButton,
  LoadingSpinner,
  ErrorMessage,
  VariationsSection,
  VariationItem,
  VariationCheckbox,
  VariationInfo,
  VariationName,
  QuantityControls,
  QtyButton,
  QtyValue
} from './ListingSelectionModal.styles';

export default function BatchListingSelectionModal({ 
  isOpen, 
  onClose, 
  cardNumbers,
  filteredCards,
  onSuccess 
}) {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [selectedSales, setSelectedSales] = useState([]);
  const [selectedVariationsByCard, setSelectedVariationsByCard] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { data: allSales = [], isLoading, error } = useGetSalesQuery(
    { uid: user?.uid },
    { skip: !user?.uid }
  );
  const [addCardToSale] = useAddCardToSaleMutation();
  const [createSale] = useCreateSaleMutation();

  // A sold sale's cards have already been released from the collection, so
  // adding to one would put it out of step with what was actually sold.
  const sales = useMemo(() => allSales.filter((sale) => !isSold(sale.status)), [allSales]);


  // Get card data for selected card numbers
  const selectedCards = useMemo(() => {
    return cardNumbers
      .map(cardNumber => filteredCards.find(card => card.cardNumber === cardNumber))
      .filter(card => card !== undefined);
  }, [cardNumbers, filteredCards]);

  const getCardVariations = (card) => {
    if (card?.variations && card.variations.length > 0) {
      return card.variations;
    }
    if (card?.collectedCards && card.collectedCards.length > 0) {
      return card.collectedCards;
    }
    return [];
  };

  // Reset selections when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedSales([]);
      const initialSelections = {};

      selectedCards.forEach(card => {
        const baseQuantity = card.quantity || 1;
        const variations = getCardVariations(card);
        const variationsToUse = variations.length > 0
          ? variations
          : [{
            cardParallelName: 'BASE',
            cardColor: '#FFFFFF',
            cardQty: baseQuantity
          }];

        const cardSelections = {};
        variationsToUse.forEach((variation, index) => {
          const variationKey = variation.cardParallelName || `variation_${index}`;
          const maxQuantity = variation.cardQty || baseQuantity || 1;
          cardSelections[variationKey] = {
            selected: true,
            quantity: 1,
            maxQuantity,
            variation
          };
        });

        initialSelections[card.cardNumber] = cardSelections;
      });

      setSelectedVariationsByCard(initialSelections);
    }
  }, [isOpen, selectedCards]);

  const handleSaleToggle = (saleId) => {
    setSelectedSales(prev =>
      prev.includes(saleId)
        ? prev.filter(id => id !== saleId)
        : [...prev, saleId]
    );
  };

  const handleVariationToggle = (cardNumber, variationKey) => {
    setSelectedVariationsByCard(prev => ({
      ...prev,
      [cardNumber]: {
        ...prev[cardNumber],
        [variationKey]: {
          ...prev[cardNumber]?.[variationKey],
          selected: !prev[cardNumber]?.[variationKey]?.selected
        }
      }
    }));
  };

  const handleQuantityChange = (cardNumber, variationKey, delta) => {
    setSelectedVariationsByCard(prev => {
      const cardSelections = prev[cardNumber];
      if (!cardSelections) return prev;

      const current = cardSelections[variationKey];
      if (!current) return prev;

      const currentQty = current.quantity || 1;
      const maxQty = current.maxQuantity || 1;
      const newQuantity = Math.max(1, Math.min(maxQty, currentQty + delta));

      return {
        ...prev,
        [cardNumber]: {
          ...cardSelections,
          [variationKey]: {
            ...current,
            quantity: newQuantity
          }
        }
      };
    });
  };

  const buildSelectedItems = () => {
    const items = [];
    selectedCards.forEach(card => {
      const selections = selectedVariationsByCard[card.cardNumber] || {};
      Object.values(selections).forEach(selection => {
        if (!selection.selected) return;
        items.push({ card, selection });
      });
    });
    return items;
  };

  // Helper function to get card data for listing
  const getCardDataForListing = (cardData, selection) => {
    const checklistPath = cardData.checklistPath || '';
    const variations = getCardVariations(cardData);
    const isCollected = cardData.isCollected !== undefined
      ? cardData.isCollected
      : (variations.length > 0);

    const baseCardData = {
      cardNumber: cardData.cardNumber,
      name: cardData.name,
      team: cardData.team || [],
      cardAttributes: cardData.cardAttributes || [],
      checklistPath,
      isCollected: isCollected,
      quantity: 1
    };

    if (selection?.variation) {
      return {
        ...baseCardData,
        variation: {
          cardParallelName: selection.variation.cardParallelName,
          cardColor: selection.variation.cardColor,
          cardQty: 1,
          cardPrintNumber: selection.variation.cardPrintNumber,
          cardSupply: selection.variation.cardSupply
        }
      };
    }

    return baseCardData;
  };

  const addSelectedItemsToSale = async (saleId, selectedItems) => {
    const addPromises = [];

    selectedItems.forEach(item => {
      const quantity = item.selection.quantity || 1;
      for (let i = 0; i < quantity; i++) {
        const cardDataForListing = getCardDataForListing(item.card, item.selection);
        cardDataForListing.quantity = 1;
        addPromises.push(
          addCardToSale({
            uid: user.uid,
            saleId,
            cardData: cardDataForListing
          })
        );
      }
    });

    await Promise.all(addPromises);
  };

  const handleAddToSales = async () => {
    if (!user?.uid) {
      dispatch(setSystemNotification('Please sign in to add to sales'));
      return;
    }

    if (selectedSales.length === 0) {
      dispatch(setSystemNotification('Select at least one sale'));
      return;
    }

    const selectedItems = buildSelectedItems();
    if (selectedItems.length === 0) {
      dispatch(setSystemNotification('Select at least one variation'));
      return;
    }

    setIsSubmitting(true);
    try {
      for (const saleId of selectedSales) {
        await addSelectedItemsToSale(saleId, selectedItems);
      }
      onSuccess?.(selectedSales);
      onClose();
    } catch (error) {
      dispatch(setSystemNotification('Error adding cards to sales'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const selectedItems = buildSelectedItems();
  const totalQuantity = selectedItems.reduce((total, item) => {
    return total + (item.selection.quantity || 1);
  }, 0);
  const hasSelectedVariations = selectedItems.length > 0;

  const handleCreateNewSale = async () => {
    if (!user?.uid) {
      dispatch(setSystemNotification('Please sign in to create a sale'));
      return;
    }

    if (!hasSelectedVariations) {
      dispatch(setSystemNotification('Select at least one variation'));
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await createSale({
        uid: user.uid,
        saleData: {
          status: SALE_STATUSES.LISTED,
          platform: '',
          price: 0
        }
      }).unwrap();

      await addSelectedItemsToSale(result.saleId, selectedItems);
      onSuccess?.([result.saleId]);
      onClose();
    } catch (error) {
      dispatch(setSystemNotification('Error creating sale'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalContent = (
    <ModalOverlay onClick={onClose} className='widget-bg'>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Add to Sale</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          {isLoading && <LoadingSpinner>Loading sales...</LoadingSpinner>}
          
          {error && <ErrorMessage>Error loading sales: {error.message}</ErrorMessage>}
          
          {sales.length === 0 && !isLoading && (
            <ErrorMessage>No active sales available. Create a sale below.</ErrorMessage>
          )}

          {selectedCards.length > 0 && (
            <>
              <VariationsSection>
                {selectedCards.map((card) => {
                  const selections = selectedVariationsByCard[card.cardNumber] || {};
                  const cardName = Array.isArray(card.name) ? card.name.join(' & ') : card.name;
                  return (
                    <div key={card.cardNumber} style={{ marginBottom: '16px' }}>
                      <p style={{ marginBottom: '8px', fontWeight: '600' }}>
                        #{card.cardNumber} {cardName || ''}
                      </p>
                      <SetsList>
                        {Object.entries(selections).map(([variationKey, selection]) => {
                          const variationName = selection?.variation?.cardParallelName || 'Base';
                          const printInfo = selection?.variation?.cardPrintNumber && selection?.variation?.cardSupply
                            ? ` (${selection.variation.cardPrintNumber}/${selection.variation.cardSupply})`
                            : '';
                          return (
                            <VariationItem key={`${card.cardNumber}-${variationKey}`}>
                              <VariationCheckbox
                                type="checkbox"
                                checked={!!selection?.selected}
                                onChange={() => handleVariationToggle(card.cardNumber, variationKey)}
                              />
                              <VariationInfo>
                                <VariationName>
                                  <CardAttribute
                                    color={selection?.variation?.cardColor || '#FFFFFF'}
                                    text={`${variationName}${printInfo}`}
                                    size="M"
                                  />
                                </VariationName>
                                {selection?.selected && (
                                  <QuantityControls>
                                    <QtyButton
                                      onClick={() => handleQuantityChange(card.cardNumber, variationKey, -1)}
                                      disabled={(selection?.quantity || 1) <= 1}
                                    >
                                      <img src={minusIcon} alt="decrease quantity" />
                                    </QtyButton>
                                    <QtyValue>
                                      {selection?.quantity || 1}
                                      {selection?.maxQuantity && (
                                        <span style={{ fontSize: '10px', opacity: 0.7, marginLeft: '4px' }}>
                                          /{selection.maxQuantity}
                                        </span>
                                      )}
                                    </QtyValue>
                                    <QtyButton
                                      onClick={() => handleQuantityChange(card.cardNumber, variationKey, 1)}
                                      disabled={(selection?.quantity || 1) >= (selection?.maxQuantity || 1)}
                                    >
                                      <img src={addIcon} alt="increase quantity" />
                                    </QtyButton>
                                  </QuantityControls>
                                )}
                              </VariationInfo>
                            </VariationItem>
                          );
                        })}
                      </SetsList>
                    </div>
                  );
                })}
              </VariationsSection>

              {sales.length > 0 && (
                <>
                  <p>
                    Select the sale you want to add ({totalQuantity}) card{totalQuantity !== 1 ? 's' : ''} to:
                  </p>
                  <SetsList>
                    {sales.map((sale) => (
                      <ListingCard
                        key={sale.id}
                        sale={sale}
                        selectable={true}
                        onSelect={() => handleSaleToggle(sale.id)}
                        onClick={() => handleSaleToggle(sale.id)}
                      />
                    ))}
                  </SetsList>
                </>
              )}
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            <AddButton 
              onClick={handleAddToSales}
              disabled={selectedSales.length === 0 || !hasSelectedVariations || isSubmitting}
            >
              {isSubmitting ? 'Adding...' : `Add to ${selectedSales.length} Sale${selectedSales.length !== 1 ? 's' : ''}`}
            </AddButton>
            <AddButton
              onClick={handleCreateNewSale}
              disabled={!hasSelectedVariations || isSubmitting}
            >
              {isSubmitting
                ? 'Creating...'
                : `CREATE NEW SALE WITH ${totalQuantity} CARD${totalQuantity !== 1 ? 'S' : ''}`}
            </AddButton>
            <CancelButton onClick={onClose}>
              Cancel
            </CancelButton>
          </ButtonGroup>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );

  return createPortal(modalContent, document.body);
}
